import { createEntityAdapter } from '@reduxjs/toolkit';
import { nanoid } from 'nanoid';

import { Column, Row, RowData, ValueType } from '../types';
import { compareCellValues, createEmptyRowData, fillEmptyValuesWithDefaults, isEmptyRow } from './helper';

// Adapter for rows
export const rowsAdapter = createEntityAdapter<Row>();

/**
 * 행 추가 시 데이터 전처리 로직
 * 
 * @param tableId - 테이블 ID
 * @param rowData - 추가할 행 데이터
 * @returns 전처리된 행 데이터
 */
export const prepareAddRow = (tableId: string, rowData: Row) => {
    const dataWithEmptyValues = fillEmptyValuesWithDefaults(rowData.data);

    const row: Row = {
        id: rowData.id || nanoid(),
        data: dataWithEmptyValues,
    };
    
    return { payload: { tableId, row } };
};

/**
 * 다중 행 추가 시 데이터 전처리 로직
 * 
 * @param tableId - 테이블 ID
 * @param rowsData - 추가할 행 데이터 배열
 * @returns 전처리된 행 데이터 배열
 */
export const prepareAddRows = (tableId: string, rowsData: Row[]) => {
    const rows: Row[] = rowsData.map((rowData) => ({
        ...rowData,
        id: rowData.id || nanoid(),
    }));
    
    return { payload: { tableId, rows } };
};

/**
 * 행 데이터 설정 시 전처리 로직
 * 
 * @param tableId - 테이블 ID
 * @param rowsData - 설정할 행 데이터 배열
 * @returns 전처리된 행 데이터 배열
 */
export const prepareSetRows = (tableId: string, rowsData: Row[]) => {
    const rows: Row[] = rowsData.map((rowData) => ({
        ...rowData,
        id: rowData.id || nanoid(),
    }));
    
    return { payload: { tableId, rows } };
};

/**
 * 단일 행 설정 시 전처리 로직
 * 
 * @param tableId - 테이블 ID
 * @param rowId - 행 ID
 * @param rowData - 설정할 행 데이터
 * @returns 전처리된 행 데이터
 */
export const prepareSetRow = (tableId: string, rowId: string, rowData: Row) => {
    const row: Row = {
        ...rowData,
        id: rowData.id || nanoid(),
    };
    
    return { payload: { tableId, rowId, row } };
};

/**
 * 행 정렬 로직
 * 
 * @param rows - 정렬할 행 배열
 * @param columnId - 정렬 기준 컬럼 ID
 * @param orderType - 정렬 순서 ('asc' | 'desc')
 * @param valueType - 컬럼 값 타입
 * @returns 정렬된 행 배열
 */
export const sortRowsLogic = (
    rows: Row[],
    columnId: string,
    orderType: string,
    valueType: ValueType
): Row[] => {
    if (orderType === undefined) return rows;

    // 빈 행과 데이터 행 분리
    const emptyRows = rows.filter(isEmptyRow);
    const dataRows = rows.filter(row => !isEmptyRow(row));

    // 데이터가 있는 행들만 정렬
    const sortedDataRows = dataRows.sort((a, b) => {
        const cellA = a.data[columnId]?.value;
        const cellB = b.data[columnId]?.value;

        const comparison = compareCellValues(cellA, cellB, valueType);
        return orderType === 'asc' ? comparison : -comparison;
    });

    // 정렬된 데이터 행 + 빈 행 순서로 재배치
    return [...sortedDataRows, ...emptyRows];
};

/**
 * 빈 행 생성 로직
 * 
 * @param columns - 컬럼 정보 객체
 * @returns 생성된 빈 행
 */
export const createEmptyRow = (columns: { [columnId: string]: Column }): Row => {
    const emptyData = createEmptyRowData(columns);
    
    return {
        id: nanoid(),
        data: emptyData
    };
};

/**
 * 초기 행 데이터 생성 로직
 * 
 * @param initialRows - 초기 행 데이터 배열
 * @returns ID가 추가된 행 배열
 */
export const createInitialRows = (initialRows: RowData[]): Row[] => {
    return initialRows.map((rowData) => ({
        id: nanoid(),
        ...rowData,
    }));
};